// ABOUTME: Relation-portal history tab — lists CLOSED tickets for the relation.
// ABOUTME: Selecting one opens it in the same chat/details layout used by the workscreen.

import { Box, InputBase, useTheme } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { useTranslation } from 'react-i18next';
import Subheading from "../../../../components/theme/text/Subheading";
import Text from "../../../../components/theme/text/Text";
import { tokens } from "../../../../theme";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { ServicedeskTicketCardWarehouse } from "../../../warehouse/relation/service/components/ServicedeskTicketCard";
import TicketDetailPanel from "../../../warehouse/relation/service/components/TicketDetailPanel";
import { useGetRelationClosedTicketsQuery } from "../../../../newapi/warehouse/supportTicketApiSlice";
import HistoryIcon from "@mui/icons-material/History";
import AddIcon from "@mui/icons-material/Add";
import ShGridEmpty from "../../../../components/theme/datagrid2/Status/ShGridEmpty";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import MAddSupportTicket from "../components/modals/MAddSupportTicket";
import { support_ticket_linked_type } from "../../../../utils/staticEnums";

const gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const RelationHistory = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isStuck, setIsStuck] = useState(false);
    const [newTicketOpen, setNewTicketOpen] = useState(false);
    const sentinelRef = useRef(null);

    const linkedItemForNewTicket = selectedTicket
        ? [{
            linkedType: support_ticket_linked_type.TICKET,
            linkedId: selectedTicket.id,
            displayName: selectedTicket.ticketNumber,
            displayReference: selectedTicket.title,
        }]
        : [];

    // Server-side searched, paginated list. The input value (searchInput) is
    // local; the committed search (search) only updates on Enter, so we don't
    // hit the backend on every keystroke.
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const submitSearch = () => setSearch(searchInput.trim());

    const [page, setPage] = useState(1);
    const [closedTickets, setClosedTickets] = useState([]);
    useEffect(() => {
        setPage(1);
        setClosedTickets([]);
    }, [search]);

    const { ref: sentinelInfiniteRef, inView: sentinelInfiniteInView } = useInView({ threshold: 1.0 });
    const { data: closedData, isFetching: closedFetching } = useGetRelationClosedTicketsQuery({
        page,
        limit: 20,
        search,
    });

    // console.log(closedData)-6

    useEffect(() => {
        if (!closedData?.data) return;
        const incomingPage = closedData?.pagination?.page ?? page;
        setClosedTickets((prev) => {
            // Page 1 = fresh result (initial load OR after search change). Replace
            // the buffer entirely so a narrower search doesn't leave stale rows.
            if (incomingPage === 1) {
                return closedData.data;
            }
            const seen = new Set(prev.map((t) => t.id));
            const incoming = closedData.data.filter((t) => !seen.has(t.id));
            return [...prev, ...incoming];
        });
    }, [closedData, page]);

    useEffect(() => {
        if (!sentinelInfiniteInView || closedFetching) return;
        const totalPages = closedData?.pagination?.totalPages;
        if (totalPages && page >= totalPages) return;
        setPage((prev) => prev + 1);
    }, [sentinelInfiniteInView, closedFetching, closedData, page]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            ([entry]) => setIsStuck(!entry.isIntersecting),
            { threshold: 0, rootMargin: '-72px 0px 0px 0px' }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions,
            styles: { main: { overflow: 'visible' } },
        });
    }, []);

    return (
        <Box sx={{
            display: "grid",
            gridTemplateColumns: "420px 1fr",
            "@media screen and (max-width: 63.9375em)": {
                gridTemplateColumns: "1fr",
            },
        }}>
            {/* Left — closed ticket list */}
            <Box sx={{
                borderRight: `2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                "@media screen and (max-width: 63.9375em)": { maxHeight: 300 },
            }}>
                <Box sx={{ pt: 4, px: 4, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Subheading>{t("history")}</Subheading>
                    <Box
                        sx={{
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            borderRadius: 4,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                        }}
                    >
                        <SearchOutlinedIcon sx={{ fontSize: 18, color: colors.txt['secondary'] }} />
                        <InputBase
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submitSearch(); }}
                            placeholder={t("search")}
                            sx={{
                                flex: 1,
                                fontSize: 14,
                                fontFamily: "Roboto, sans-serif",
                                color: colors.txt['primary'],
                            }}
                        />
                    </Box>
                </Box>

                {closedTickets.length > 0 ? (
                    <Box>
                        {closedTickets.map((ticket) => (
                            <ServicedeskTicketCardWarehouse
                                key={ticket.id}
                                ticket={{
                                    id: ticket.id,
                                    ticketNumber: ticket.ticketNumber,
                                    title: ticket.title,
                                    type: ticket.type,
                                    priority: ticket.priority?.toLowerCase(),
                                    lastUpdateTime: new Date(ticket.closedAt || ticket.updatedAt).toLocaleTimeString(),
                                    relationName: ticket.relation?.name || 'Unknown',
                                    contactName: ticket.relation?.contactName || 'Unknown',
                                    updates: [],
                                }}
                                onClick={() => setSelectedTicket(ticket)}
                                selected={selectedTicket?.id === ticket.id}
                            />
                        ))}
                        <Box ref={sentinelInfiniteRef} sx={{ height: 1 }} />
                    </Box>
                ) : (
                    !closedFetching && (
                        <Box sx={{ pt: 6, px: 4 }}>
                            <ShGridEmpty
                                titleKey={search ? "no_results" : "no_closed_tickets"}
                                descriptionKey={search ? "no_results_description" : "no_closed_tickets_description"}
                            />
                        </Box>
                    )
                )}
            </Box>

            {/* Right — selected ticket detail (read-only) */}
            <Box sx={{ alignSelf: 'stretch' }}>
                <Box ref={sentinelRef} sx={{ height: 0 }} />
                <Box sx={{
                    display: 'flex',
                    borderTopRightRadius: 24,
                    position: 'sticky',
                    top: 72,
                    height: isStuck ? 'calc(100vh - 72px)' : 'calc(100vh - 320px)',
                    transition: 'height 0.2s ease',
                    "@media screen and (max-width: 63.9375em)": {
                        position: 'static', height: 'auto', maxHeight: 'none', overflow: 'visible',
                    },
                }}>
                    {!selectedTicket ? (
                        <Box sx={{
                            flex: 1, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', px: 6,
                            textAlign: 'center', color: colors.txt['secondary'],
                        }}>
                            <Box sx={{
                                width: 72, height: 72, borderRadius: '50%',
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: colors.txt['primary'], mb: 2,
                            }}>
                                <HistoryIcon sx={{ fontSize: 36 }} />
                            </Box>
                            <Subheading>{t("no_ticket_selected")}</Subheading>
                            <Text align="center" light sx={{ maxWidth: 360 }}>
                                {t("select_closed_ticket_to_view")}
                            </Text>
                        </Box>
                    ) : (
                        <TicketDetailPanel
                            ticketId={selectedTicket.id}
                            viewer="client"
                            readOnly
                            readOnlyFooter={
                                <Shbutton
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setNewTicketOpen(true)}
                                    className="block"
                                >
                                    {t("new_ticket_from_this")}
                                </Shbutton>
                            }
                        />
                    )}
                </Box>
            </Box>

            {newTicketOpen && (
                <MAddSupportTicket
                    open={newTicketOpen}
                    handleClose={() => setNewTicketOpen(false)}
                    initialLinkedItems={linkedItemForNewTicket}
                />
            )}
        </Box>
    );
};

export default RelationHistory;
